import { memo, useCallback, useState } from 'react';
import { useDispatch, useSelector } from '@bonanza-social/toolkit/state';
import { useTranslation } from '@bonanza-social/i18n';
import {
  enableBiometrics,
  disableBiometrics,
  selectBiometricsEnabled,
  selectBiometricsType,
} from '@bonanza-social/auth/state';
import { Box, Button, Switch, Text, Toast } from '@bonanza-social/moonlight';

function AccountBiometricLogin() {
  const dispatch = useDispatch();
  const enabled = useSelector(selectBiometricsEnabled);
  const biometryType = useSelector(selectBiometricsType);
  const [loading, setLoading] = useState(false);
  const [localEnabled, setLocalEnabled] = useState(enabled);
  const [t] = useTranslation('auth');

  const handleButtonPress = useCallback(async () => {
    if (enabled === localEnabled) {
      return;
    }

    setLoading(true);

    const response = await (localEnabled
      ? dispatch(enableBiometrics())
      : dispatch(disableBiometrics()));

    setLoading(false);

    if (response.meta.requestStatus === 'fulfilled') {
      setTimeout(() => {
        Toast.show({
          type: 'success',
          text: localEnabled
            ? t('biometricsEnabled', { context: biometryType })
            : t('biometricsDisabled', { context: biometryType }),
        });
      }, 1000);
    } else {
      setLocalEnabled(enabled);
    }
  }, [enabled, localEnabled, biometryType, dispatch, t]);

  if (!biometryType) {
    return;
  }

  return (
    <Box
      gap="space-800"
      paddingTop={{
        desktop: undefined,
        tablet: 'space-400',
        mobile: 'space-400',
      }}
      padding={{
        desktop: 'space-400',
        tablet: 'space-400',
        mobile: undefined,
      }}
    >
      <Box flexDirection="row" justifyContent="space-between">
        <Box flex={1} gap="space-200">
          <Text
            variant="button-small"
            locNS="account"
            locKey={`enableBiometricLoginTitle_${biometryType}`}
            color="darkSurfacePrimary"
          />
          <Text
            variant="caption"
            locNS="account"
            locKey="enableBiometricLoginSubtitle"
            color="darkSurfaceSecondary"
          />
        </Box>
        <Switch value={localEnabled} onValue={setLocalEnabled} />
      </Box>
      <Button
        locNS="common"
        locKey="save"
        variant="secondary"
        loading={loading}
        disabled={enabled === localEnabled}
        onPress={handleButtonPress}
      />
    </Box>
  );
}

export default memo(AccountBiometricLogin);
